import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { CapacityTile } from "@/components/common/CapacityTile";
import { RegionStorageStatsDto } from "@/types/admin-storage";
import { formatBytes } from "@/utils/formatBytes";
import { formatRelative } from "../utils/relativeTime";

interface StorageRegionHeaderProps {
  stats: RegionStorageStatsDto;
}

export const StorageRegionHeader: React.FC<StorageRegionHeaderProps> = ({
  stats,
}) => {
  const { t } = useTranslation();

  if (!stats.reachable) {
    return (
      <section className="flex flex-col gap-4">
        <div className="rounded-lg border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-4 flex items-start gap-3">
          <AlertTriangle className="size-5 text-custom-destructive-dark mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-custom-destructive-dark">
              {t("storage.region.unreachable.title")}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("storage.region.unreachable.description", {
                message: stats.error ?? "—",
              })}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <CapacityTile label={t("storage.region.tiles.total")} value="—" />
          <CapacityTile label={t("storage.region.tiles.used")} value="—" />
          <CapacityTile label={t("storage.region.tiles.free")} value="—" />
          <CapacityTile label={t("storage.region.tiles.usage")} value="—" />
        </div>
      </section>
    );
  }

  const usagePercentDisplay =
    stats.usagePercent === null
      ? "—"
      : `${(stats.usagePercent * 100).toFixed(1)}%`;
  const usagePercentBar =
    stats.usagePercent === null ? null : stats.usagePercent * 100;

  return (
    <section className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <CapacityTile
          label={t("storage.region.tiles.total")}
          value={formatBytes(stats.totalBytes)}
        />
        <CapacityTile
          label={t("storage.region.tiles.used")}
          value={formatBytes(stats.usedBytes)}
          hint={usagePercentDisplay}
          bar={{ percent: usagePercentBar }}
        />
        <CapacityTile
          label={t("storage.region.tiles.free")}
          value={formatBytes(stats.freeBytes)}
        />
        <CapacityTile
          label={t("storage.region.tiles.usage")}
          value={usagePercentDisplay}
          bar={{ percent: usagePercentBar }}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CapacityTile
          label={t("storage.region.tiles.objectCount")}
          value={
            stats.objectCount === null
              ? "—"
              : stats.objectCount.toLocaleString()
          }
        />
        <CapacityTile
          label={t("storage.region.tiles.bucketCount")}
          value={
            stats.bucketCount === null
              ? "—"
              : stats.bucketCount.toLocaleString()
          }
        />
        <CapacityTile
          label={t("storage.region.tiles.objectBytes")}
          value={formatBytes(stats.objectBytes)}
        />
      </div>
      <span className="text-xs text-muted-foreground">
        {t("storage.region.fetched", {
          time: formatRelative(stats.fetchedAt),
        })}
      </span>
    </section>
  );
};
